import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getToken, logout, getRole, getUserId } from "../utils/auth";
import HumanLoader from "../components/loaders/HumanLoader";
import "./QuestionListPage.css";

export default function QuestionListPage() {
  const { categoryId } = useParams();
  const [questions, setQuestions] = useState([]);
  const [solvedMap, setSolvedMap] = useState({});
  const [submissionsMap, setSubmissionsMap] = useState({});
  const [pageLoading, setPageLoading] = useState(true);
const [currentPage, setCurrentPage] = useState(1);
const questionsPerPage = 15;
  const [search, setSearch] = useState("");
  const [difficultyFilter, setDifficultyFilter] = useState("");

  /* MODAL STATE */
  const [selectedQuestionTitle, setSelectedQuestionTitle] = useState("");
  const [activeSubmissions, setActiveSubmissions] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const navigate = useNavigate();

  /* FETCH QUESTIONS */
  useEffect(() => {
    const fetchQuestions = async () => {
      const startTime = Date.now();
      setPageLoading(true);

      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/questions?categoryId=${categoryId}`,
          {
            headers: {
              Authorization: `Bearer ${getToken()}`
            }
          }
        );

        if (res.status === 401 || res.status === 403) {
          logout();
          navigate("/login");
          return;
        }

        const data = await res.json();
        setQuestions(data);

        await checkSolvedStatus(data);
      } catch (err) {
        console.error("Failed to fetch questions", err);
      } finally {
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(2000 - elapsed, 0);
        setTimeout(() => setPageLoading(false), remaining);
      }
    };

    fetchQuestions();
  }, [categoryId, navigate]);
  useEffect(() => {
    setCurrentPage(1);
  }, [search, difficultyFilter]);
  /* CHECK SOLVED STATUS */
  const checkSolvedStatus = async (questions) => {
    const solvedStatus = {};
    const submissionsData = {};

    await Promise.all(
      questions.map(async (q) => {
        try {
          const res = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/api/submissions/solved?questionId=${q._id}&userId=${getUserId()}`,
            {
              headers: {
                Authorization: `Bearer ${getToken()}`
              }
            }
          );

          if (res.ok) {
            const data = await res.json();
            solvedStatus[q._id] = data.solved;

            if (data.solved && data.submission) {
              submissionsData[q._id] = Array.isArray(data.submission)
                ? data.submission
                : [data.submission];
            }
          }
        } catch (err) {
          console.error("Solved check failed", err);
        }
      })
    );

    setSolvedMap(solvedStatus);
    setSubmissionsMap(submissionsData);
  };

  /* OPEN MODAL */
  const handleOpenSubmissions = (question) => {
    const subs = submissionsMap[question._id] || [];
    setSelectedQuestionTitle(question.title);
    setActiveSubmissions(subs);
    setActiveTab(0);
    setIsModalOpen(true);
  };

  /* CLOSE MODAL */
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setActiveSubmissions([]);
    setSelectedQuestionTitle("");
  };

  const handleDeleteQuestion = async (question) => {
    if (!window.confirm(`Are you sure you want to delete "${question.title}"?`)) {
      return;
    }

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/questions/${question._id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${getToken()}`
          }
        }
      );

      if (res.status === 401 || res.status === 403) {
        logout();
        navigate("/login");
        return;
      }

      if (!res.ok) {
        throw new Error("Failed to delete question.");
      }

      setQuestions((currentQuestions) =>
        currentQuestions.filter((item) => item._id !== question._id)
      );
      setSolvedMap((currentSolvedMap) => {
        const updated = { ...currentSolvedMap };
        delete updated[question._id];
        return updated;
      });
      setSubmissionsMap((currentSubmissionsMap) => {
        const updated = { ...currentSubmissionsMap };
        delete updated[question._id];
        return updated;
      });
    } catch (err) {
      console.error("Failed to delete question", err);
      window.alert(err.message || "Failed to delete question.");
    }
  };

  if (pageLoading) {
    return (
      <HumanLoader
        loadingText="Preparing problems"
        successText="Ready to practice!"
        duration={2000}
      />
    );
  }

  /* COUNTS */
  const solvedCount = Object.values(solvedMap).filter(Boolean).length;
  const pendingCount = questions.length - solvedCount;

  /* FILTERED & SORTED QUESTIONS (Unsolved First) */
  const filteredQuestions = questions
    .filter((q) => {
      const matchesSearch = q.title
        .toLowerCase()
        .includes(search.toLowerCase());

      const matchesDifficulty = difficultyFilter
        ? q.difficulty === difficultyFilter
        : true;

      return matchesSearch && matchesDifficulty;
    })
    .sort((a, b) => {
      const isASolved = Boolean(solvedMap[a._id]);
      const isBSolved = Boolean(solvedMap[b._id]);

      // False (unsolved) comes before True (solved)
      return isASolved === isBSolved ? 0 : isASolved ? 1 : -1;
    });

  const currentSubmission = activeSubmissions[activeTab];

const totalPages = Math.ceil(filteredQuestions.length / questionsPerPage);
const startIndex = (currentPage - 1) * questionsPerPage;
const endIndex = startIndex + questionsPerPage;

const paginatedQuestions = filteredQuestions.slice(
  startIndex,
  endIndex
);
  const canManageQuestions = ["admin", "subadmin"].includes(getRole());
  return (
    <div className="ql-page">
      <div className="ql-container">

        <div className="ql-header">
          <h1>Practice Problems</h1>
          <p>Solve problems from this category</p>
        </div>

        {/* STATS */}
        <div className="ql-stats">
          <div className="stat-card">Total: {questions.length}</div>
          <div className="stat-card solved">Solved: {solvedCount}</div>
          <div className="stat-card pending">Pending: {pendingCount}</div>
        </div>

        {/* SEARCH + FILTER */}
        <div className="ql-controls">
          <input
            type="text"
            placeholder="Search questions..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <select
            value={difficultyFilter}
            onChange={(e) => setDifficultyFilter(e.target.value)}
          >
            <option value="">All Difficulty</option>
            <option value="Easy">Easy</option>
            <option value="Medium">Medium</option>
            <option value="Hard">Hard</option>
          </select>
        </div>

        {/* TABLE */}
        <table className="ql-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Title</th>
              <th>Difficulty</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
           {paginatedQuestions.map((q, index) => {
              const isSolved = solvedMap[q._id];

              return (
                <tr key={q._id}>
                 <td>{startIndex + index + 1}</td>

                  <td>{q.title}</td>

                  <td>
                    <span className={`ql-badge ${q.difficulty.toLowerCase()}`}>
                      {q.difficulty}
                    </span>
                  </td>

                  <td>
                    {isSolved ? (
                      <span className="status solved">Solved</span>
                    ) : (
                      <span className="status pending">Pending</span>
                    )}
                  </td>

                  <td>
                    {canManageQuestions && (
                      <button
                        className="ql-edit-btn"
                        onClick={() => navigate(`/admin/edit/${q._id}`)}
                      >
                        Edit
                      </button>
                    )}

                    {canManageQuestions && (
                      <button
                        className="ql-delete-btn"
                        onClick={() => handleDeleteQuestion(q)}
                      >
                        Delete
                      </button>
                    )}

                    {isSolved && (
                      <button
                        className="ql-sub-btn"
                        onClick={() => handleOpenSubmissions(q)}
                      >
                        Submissions
                      </button>
                    )}

                    <button
                      className="ql-solve-btn"
                      onClick={() => navigate(`/questions/${q._id}`)}
                    >
                      Solve →
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
          <div className="ql-pagination">
            <button
              onClick={() => setCurrentPage((prev) => prev - 1)}
              disabled={currentPage === 1}
              className="page-btn"
            >
              Previous
            </button>

            <span className="page-info">
              Page {currentPage} of {totalPages || 1}
            </span>

            <button
              onClick={() => setCurrentPage((prev) => prev + 1)}
              disabled={currentPage === totalPages || totalPages === 0}
              className="page-btn"
            >
              Next
            </button>
          </div>
      </div>

      {/* SUBMISSION MODAL */}
      {isModalOpen && (
        <div className="ql-modal-overlay" onClick={handleCloseModal}>
          <div className="ql-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="ql-modal-header">
              <h2>Submissions: {selectedQuestionTitle}</h2>
              <button className="ql-modal-close" onClick={handleCloseModal}>
                &times;
              </button>
            </div>

            {/* TABS FOR MULTIPLE SUBMISSIONS */}
            {activeSubmissions.length > 1 && (
              <div className="ql-modal-tabs">
                {activeSubmissions.map((sub, idx) => (
                  <button
                    key={sub._id || idx}
                    className={`ql-tab-btn ${activeTab === idx ? "active" : ""}`}
                    onClick={() => setActiveTab(idx)}
                  >
                    Submission #{idx + 1}
                  </button>
                ))}
              </div>
            )}

            {currentSubmission ? (
              <div className="ql-modal-body">
                <div className="ql-sub-meta">
                  <span><strong>Language:</strong> {currentSubmission.language}</span>
                  <span><strong>Verdict:</strong> <span className={`verdict ${currentSubmission.verdict?.toLowerCase()}`}>{currentSubmission.verdict}</span></span>
                  <span><strong>Passed:</strong> {currentSubmission.passed} / {currentSubmission.total}</span>
                  <span><strong>Submitted:</strong> {new Date(currentSubmission.createdAt).toLocaleString()}</span>
                </div>

                <div className="ql-code-container">
                  <pre>
                    <code>{currentSubmission.code}</code>
                  </pre>
                </div>
              </div>
            ) : (
              <p>No submission data found.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}